import ITabletopShapeRenderer from "./ITabletopShapeRenderer";
import Shape2d from "../../../../../common/geometry/Shape2d";
import { Color } from "../../../../../utils/sys/Color";
import ImageLoader from "../../../../../common/utilities/ImageLoader";
import { Dictionary } from "../../../../../utils/sys/Dictionary";
import { v4 as uuidv4 } from "uuid";

export default class TabletopSimpleShapeRenderer implements ITabletopShapeRenderer {
    private static imageLibrary: Dictionary<string, ImageBitmap> =
        new Dictionary();

    private _id: string;

    protected _outlineWidth: number;
    protected _outlineStyle:
        | Color
        | string
        | CanvasGradient
        | CanvasPattern
        | null;
    protected _fillColor?: Color | string | null;
    protected _fillGradient?: CanvasGradient | null;
    protected _fillTextureSrc?: string | null;

    private _fillPattern: CanvasPattern | null = null;

    public constructor(args: {
        id?: string;
        outlineWidth?: number;
        outlineStyle?: Color | string | CanvasGradient | CanvasPattern;
        fillColor?: Color | string;
        fillGradient?: CanvasGradient;
        fillTextureSrc?: string;
    }) {
        this._id = args.id ?? uuidv4();
        this._outlineWidth = args.outlineWidth ?? 0;
        this._outlineStyle = args.outlineStyle ?? null;
        this._fillColor = args.fillColor ?? null;
        this._fillGradient = args.fillGradient ?? null;
        this._fillTextureSrc = args.fillTextureSrc ?? null;

        if (args.fillTextureSrc != null) {
            const src = args.fillTextureSrc;
            ImageLoader.loadImage(args.fillTextureSrc, (img) => {
                TabletopSimpleShapeRenderer.imageLibrary.set(src, img);
            });
        }
    }

    public get id() {
        return this._id;
    }

    public get fillTextureSrc() {
        return this._fillTextureSrc;
    }

    public getTexture(): ImageBitmap | null {
        if (this._fillTextureSrc == null) {
            return null;
        }

        return (
            TabletopSimpleShapeRenderer.imageLibrary.get(
                this._fillTextureSrc,
            ) ?? null
        );
    }

    public drawShape(ctx: CanvasRenderingContext2D, shape: Shape2d): void {
        if (shape.vertices.length === 0) {
            return;
        } else if (shape.vertices.length === 1) {
            const origin = shape.firstVertex;

            if (origin == null) {
                return;
            }

            ctx.beginPath();
            ctx.moveTo(origin.x, origin.y);
            ctx.lineTo(origin.x, origin.y + 1);

            ctx.strokeStyle = "black";
            ctx.lineWidth = 1;
            ctx.stroke();

            return;
        } else if (shape.vertices.length === 2) {
            const origin = shape.firstVertex;
            const end = shape.lastVertex;

            if (origin == null || end == null) {
                return;
            }

            ctx.beginPath();
            ctx.moveTo(origin.x, origin.y);
            ctx.lineTo(end.x, end.y);

            ctx.strokeStyle = "black";
            ctx.lineWidth = 1;
            ctx.stroke();

            return;
        }

        if (this._fillPattern == null && this._fillTextureSrc != null) {
            const texture = TabletopSimpleShapeRenderer.imageLibrary.get(
                this._fillTextureSrc,
            );

            if (texture != null) {
                this._fillPattern = ctx.createPattern(texture, "repeat");
            }
        }

        ctx.beginPath();

        for (let idx = 0; idx < shape.vertices.length; ++idx) {
            const vert = shape.vertex(idx) ?? {
                x: 0,
                y: 0,
            };

            if (idx === 0) {
                ctx.moveTo(vert.x, vert.y);
            } else {
                ctx.lineTo(vert.x, vert.y);
            }
        }

        ctx.closePath();

        if (this._fillColor != null) {
            ctx.fillStyle = this._fillColor;
            ctx.fill();
        }

        if (this._fillGradient != null) {
            ctx.fillStyle = this._fillGradient;
            ctx.fill();
        }

        if (this._fillPattern != null) {
            ctx.fillStyle = this._fillPattern;
            ctx.fill();
        }

        if (this._outlineStyle != null && this._outlineWidth > 0) {
            ctx.strokeStyle = this._outlineStyle;
            ctx.lineWidth = this._outlineWidth;

            ctx.stroke();
        }
    }
}
